import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { getAuth, sendPasswordResetEmail } from 'firebase/auth';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db } from '../../firebase';
import './users.css';
import settingDP from '../../images/setting.png';
import '@fortawesome/fontawesome-free/css/all.min.css'; // Import FontAwesome
import { FaPlusCircle } from 'react-icons/fa'; // Import FontAwesome icon

const UserInfo = () => {
    const { userId } = useParams();  // Get the userId from the URL parameters
    const navigate = useNavigate(); // Initialize useNavigate
    const [user, setUser] = useState(null);  // State to store user data
    const [loading, setLoading] = useState(true);  // State to manage loading state
    const [editMode, setEditMode] = useState(false);  // State to toggle edit mode
    const [updatedUser, setUpdatedUser] = useState({});  // State to store updated user data
    const [successMessage, setSuccessMessage] = useState('');  // State to store success messages
    const [profilePictureUrl, setProfilePictureUrl] = useState('');  // State to store profile picture URL
    const [uploadError, setUploadError] = useState('');  // State to store upload errors
    const [balance, setBalance] = useState(0); // State to store the balance
    const fileInputRef = useRef(null); // Create a reference for the file input

    useEffect(() => {
        // * Function to fetch user data from Firestore
        const fetchUser = async () => {
            try {
                const userDoc = await getDoc(doc(db, 'users', userId));
                if (userDoc.exists()) {
                    const userData = userDoc.data();
                    setUser(userData);
                    setUpdatedUser(userData);
                    setProfilePictureUrl(userData.profilePictureUrl || '');

                    // Calculate the balance
                    const userTransactions = userData.transactions || [];
                    const userBalance = userTransactions.reduce((total, transaction) => {
                        return transaction.type === 'credit' ? total + transaction.amount : total - transaction.amount;
                    }, 0);
                    setBalance(userBalance); // Set the balance

                } else {
                    console.log('No such document!');
                }
                setLoading(false);  // Set loading state to false
            } catch (error) {
                console.error('Error fetching user: ', error);
                setLoading(false);  // Set loading state to false in case of error
            }
        };

        fetchUser();
    }, [userId]);  // Fetch user data when userId changes

    // * change value on input
    const handleChange = (e) => {
        const { name, value } = e.target;
        setUpdatedUser({ ...updatedUser, [name]: value });  // Update user data state when input changes
    };

    // * handle saves after editing
    const handleSave = async () => {
        try {
            await updateDoc(doc(db, 'users', userId), updatedUser);  // Update user document in Firestore
            setUser(updatedUser);  // Set user state to updated data
            setEditMode(false);  // Exit edit mode
            setSuccessMessage('Update successful!');  // Set success message
            console.log('Update successful');
            setTimeout(() => {
                setSuccessMessage('');  // Clear success message after 3 seconds
            }, 3000);
        } catch (error) {
            console.error('Error updating user: ', error);
        }
    };

    // * handle forgot password
    const handleForgotPassword = async () => {
        const auth = getAuth();

        if (!user || !user.email) {
            setUploadError('User email not found.');  // Set error if user email is not found
            return;
        }

        try {
            await sendPasswordResetEmail(auth, user.email);  // Send password reset email
            setSuccessMessage('Password reset email sent successfully to ' + user.email);  // Set success message
            setTimeout(() => {
                setSuccessMessage('');  // Clear success message after 8 seconds
            }, 8000);
        } catch (error) {
            setUploadError(`Error sending password reset email: ${error.message}`);  // Set error message
            console.error('Error sending password reset email:', error.message);
        }
    };

    // * state to handle the change of profile pic file
    const handleFileChange = (e) => {
        if (e.target.files[0]) {
            handleUpload(e.target.files[0]); // Automatically upload the selected file
        }
    };

    // * state to handle upload of profile pic
    const handleUpload = async (file) => {
        const storage = getStorage();
        const storageRef = ref(storage, `profilePictures/${userId}/${file.name}`);  // Create storage reference

        try {
            const snapshot = await uploadBytes(storageRef, file);  // Upload profile picture
            const downloadURL = await getDownloadURL(snapshot.ref);  // Get download URL

            // Update the Firestore document with the new profile picture URL
            await updateDoc(doc(db, 'users', userId), { profilePictureUrl: downloadURL });
            setProfilePictureUrl(downloadURL);  // Set profile picture URL state

            setSuccessMessage('Profile picture uploaded successfully.');  // Set success message
            setTimeout(() => {
                setSuccessMessage('');  // Clear success message after 3 seconds
            }, 3000);
        } catch (error) {
            setUploadError(`Error uploading profile picture: ${error.message}`);  // Set error message
            console.error('Error uploading profile picture:', error.message);
        }
    };

    const handleGoBack = () => {
        navigate(-1); // Navigate back to the previous page
    };

    if (loading) {
        return <div>Loading...</div>;  // Show loading message if data is still loading
    }

    if (!user) {
        return <div>User not found</div>;  // Show message if user data is not found
    }

    return (
        <div className="user-info">
            {/* Intro User Information */}
            <div className="container">
                <div className="row">
                    {/* Profile Image Section */}
                    <div className="col-12 col-md-4 dpClass">
                        <div className='profile-picture-container'>
                            <div className='profile-picture-wrapper'>
                                <img 
                                    src={profilePictureUrl || settingDP} 
                                    alt="Profile" 
                                    className="profile-picture" 
                                />
                                <input 
                                    type="file" 
                                    accept="image/*" 
                                    onChange={handleFileChange} 
                                    ref={fileInputRef} // Bind the input to the ref
                                    className="file-input" 
                                    style={{ display: 'none' }} // Hide the file input
                                />
                                <FaPlusCircle 
                                    className="upload-icon" 
                                    onClick={() => fileInputRef.current.click()} // Simulate click on file input
                                />
                            </div>
                            {uploadError && <p className="error-message">{uploadError}</p>}
                        </div>
                    </div>

                    {/* Second Column for Basic Info */}
                    <div className="col-12 col-md-8">
                        {/* Username */}
                        <b className='username'>Username: {user.username}</b> <hr />
                        {/* Success message for password reset */}
                        {successMessage && <p className="success-message">{successMessage}</p>}
                        {/* User's Email */}
                        <b>Email: {user.email} <i onClick={handleForgotPassword} className="reset fas fa-repeat"></i></b><hr />
                        {/* User's Account Balance */}
                        <b>Account Balance: ${balance.toFixed(2)} </b>
                        
                       <div className='username'> <Link className='reset' to={`/user/${userId}/transactions`}>View Transaction Log</Link></div>

                    </div>

                </div>
            </div>
            {/* User Basic Info Ends */}

            <div>
                <h3 className='username'>User Details</h3>
                {/* User Full Information */}
                <table>
                    <tbody>
                        {/* Full Name Row */}
                        <tr className='tr'>
                            <td><b>Full Name:</b></td>
                            <td>
                                {editMode ? (
                                    <input
                                        type="text"
                                        name="fullName"
                                        value={updatedUser.fullName}
                                        onChange={handleChange}
                                    />
                                ) : (
                                    <>{user.fullName}</>
                                )}
                            </td>
                        </tr>

                        {/* Home Address */}
                        <tr>
                            <td><b>Address:</b></td>
                            <td>
                                {editMode ? (
                                    <input
                                        type="text"
                                        name="address"
                                        value={updatedUser.address}
                                        onChange={handleChange}
                                    />
                                ) : (
                                    <>{user.address}</>
                                )}
                            </td>
                        </tr>

                        {/* User Role */}
                        <tr className='tr'>
                            <td><b>Role:</b></td>
                            <td>
                                {editMode ? (
                                    <select
                                        name="role"
                                        value={updatedUser.role}
                                        onChange={handleChange}
                                    >
                                        <option value="user">user</option>
                                        <option value="admin">admin</option>
                                    </select>
                                ) : (
                                    <>{user.role}</>
                                )}
                            </td>
                        </tr>

                        {/* Gender */}
                        <tr>
                            <td><b>Gender:</b></td>
                            <td>
                                {editMode ? (
                                    <select
                                        name="gender"
                                        value={updatedUser.gender}
                                        onChange={handleChange}
                                    >
                                        <option value="male">male</option>
                                        <option value="female">female</option>
                                    </select>
                                ) : (
                                    <>{user.gender}</>
                                )}
                            </td>
                        </tr>

                        {/* Account Number */}
                        <tr className='tr'>
                            <td><b>Account Number:</b></td>
                            <td>
                                {editMode ? (
                                    <input
                                        type="text"
                                        name="accountNumber"
                                        value={updatedUser.accountNumber}
                                        onChange={handleChange}
                                    />
                                ) : (
                                    <>{user.accountNumber}</>
                                )}
                            </td>
                        </tr>

                        {/* Transaction Report Message */}
                        <tr>
                            <td><b>Transaction Report:</b></td>
                            <td>
                                {editMode ? (
                                    <input
                                        type="text"
                                        name="report"
                                        value={updatedUser.report}
                                        onChange={handleChange}
                                    />
                                ) : (
                                    <>{user.report}</>
                                )}
                            </td>
                        </tr>

                        {/* User Group */}
                        <tr className='tr'>
                            <td><b>User Group:</b></td>
                            <td>
                                {editMode ? (
                                    <select
                                        name="group"
                                        value={updatedUser.group}
                                        onChange={handleChange}
                                    >
                                        <option value="">- Select User Role -</option>
                                        <option value="activated">activated</option>
                                        <option value="deactivated">deactivated</option>
                                    </select>
                                ) : (
                                    <>{user.group}</>
                                )}
                            </td>
                        </tr>

                        {/* Special Code */}
                        <tr>
                            <td><b>Special Code:</b></td>
                            <td>
                                {editMode ? (
                                    <input
                                        type="text"
                                        name="code"
                                        value={updatedUser.code}
                                        onChange={handleChange}
                                    />
                                ) : (
                                    <>{user.code}</>
                                )}
                            </td>
                        </tr>
                    </tbody>
                </table>
                {editMode ? (
                    // * Display if User is Editing
                    <button onClick={handleSave}>Save</button>
                ) : (
                    // * else display to edit
                    <button onClick={() => setEditMode(true)}>Edit</button>
                )}
                {successMessage && <p className="success-message">{successMessage}</p>}

                <div className="center">
                <button onClick={handleGoBack}>Go Back</button> {/* Go Back button */}
            </div>
            </div>
        </div>
    );
};

export default UserInfo;
